import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  IconAlertCircle,
  IconDeviceFloppy,
  IconX,
} from "@tabler/icons-react";
import { UnassignedTeamsPanel } from "./UnassignedTeamsPanel";
import { useSavePins } from "./useSavePins";
import type {
  SeedingEntrySlot,
  SeedingPinState,
  SeedingTeam,
} from "./seeding.types";

interface SeedingSlotPinTabProps {
  readonly stepId: string;
  readonly teams: readonly SeedingTeam[];
  readonly entrySlots: readonly SeedingEntrySlot[];
  readonly initialPins: readonly SeedingPinState[];
  readonly onPinsSaved?: (pins: readonly SeedingPinState[]) => void;
}

const UNASSIGNED_DROP_ID = "unassigned-slot";

// slot droppableId shape: "slot:<gameId>:<slotIndex>"
const slotDroppableId = (gameId: string, slotIndex: number): string =>
  `slot:${gameId}:${slotIndex}`;

const parseSlotId = (
  id: string,
): { gameId: string; slotIndex: number } | null => {
  if (!id.startsWith("slot:")) return null;
  const parts = id.split(":");
  if (parts.length !== 3) return null;
  const slotIndex = Number.parseInt(parts[2], 10);
  if (!Number.isFinite(slotIndex)) return null;
  return { gameId: parts[1], slotIndex };
};

export const SeedingSlotPinTab = ({
  stepId,
  teams,
  entrySlots,
  initialPins,
  onPinsSaved,
}: SeedingSlotPinTabProps) => {
  // Slot pins only (gameId + slotIndex). Group-only pins are preserved in
  // `nonSlotPins` so they survive a save from this tab.
  const initialAssignments = useMemo(() => {
    const byTeam = new Map<string, { gameId: string; slotIndex: number }>();
    for (const pin of initialPins) {
      if (pin.gameId && pin.slotIndex != null) {
        byTeam.set(pin.teamId, {
          gameId: pin.gameId,
          slotIndex: pin.slotIndex,
        });
      }
    }
    return byTeam;
  }, [initialPins]);

  const [assignments, setAssignments] = useState<
    Map<string, { gameId: string; slotIndex: number }>
  >(initialAssignments);

  useEffect(() => {
    setAssignments(new Map(initialAssignments));
  }, [initialAssignments]);

  const nonSlotPins = useMemo(
    () =>
      initialPins.filter(
        (p) => !(p.gameId && p.slotIndex != null),
      ),
    [initialPins],
  );

  const { save, saving } = useSavePins({
    stepId,
    successTitle: "Slot pins saved",
    successMessage: "Teams have been pinned to the selected game slots.",
    onSaved: onPinsSaved,
  });

  const hasChanges = useMemo(() => {
    if (assignments.size !== initialAssignments.size) return true;
    for (const [teamId, slot] of assignments) {
      const initial = initialAssignments.get(teamId);
      if (
        !initial ||
        initial.gameId !== slot.gameId ||
        initial.slotIndex !== slot.slotIndex
      ) {
        return true;
      }
    }
    return false;
  }, [assignments, initialAssignments]);

  const unpinnedTeams = useMemo(
    () => teams.filter((t) => !assignments.has(t.id)),
    [teams, assignments],
  );

  const teamBySlot = useMemo(() => {
    const out = new Map<string, SeedingTeam>();
    const byTeamId = new Map(teams.map((t) => [t.id, t]));
    for (const [teamId, slot] of assignments) {
      const team = byTeamId.get(teamId);
      if (team) out.set(slotDroppableId(slot.gameId, slot.slotIndex), team);
    }
    return out;
  }, [teams, assignments]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    const next = new Map(assignments);
    if (destination.droppableId === UNASSIGNED_DROP_ID) {
      next.delete(draggableId);
      setAssignments(next);
      return;
    }
    const parsed = parseSlotId(destination.droppableId);
    if (!parsed) return;

    // If another team is already pinned to that exact slot, swap: the evicted
    // team becomes unpinned.
    for (const [teamId, slot] of next) {
      if (
        slot.gameId === parsed.gameId &&
        slot.slotIndex === parsed.slotIndex &&
        teamId !== draggableId
      ) {
        next.delete(teamId);
        break;
      }
    }
    next.set(draggableId, {
      gameId: parsed.gameId,
      slotIndex: parsed.slotIndex,
    });
    setAssignments(next);
  };

  const handleUnpin = (teamId: string) => {
    const next = new Map(assignments);
    next.delete(teamId);
    setAssignments(next);
  };

  const handleSave = async (): Promise<void> => {
    const pins: SeedingPinState[] = [
      ...Array.from(assignments.entries()).map(([teamId, slot]) => ({
        teamId,
        gameId: slot.gameId,
        slotIndex: slot.slotIndex,
      })),
      ...nonSlotPins,
    ];
    await save(pins);
  };

  if (entrySlots.length === 0) {
    return (
      <Alert
        color="yellow"
        variant="light"
        icon={<IconAlertCircle size={16} />}
      >
        Step must be generated before slot pins can be assigned.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Text fw={600}>Slot pins</Text>
          <Text size="xs" c="dimmed">
            Drag a team onto a specific entry-round slot to anchor it to that
            position. Slot pins take precedence over the preset&apos;s
            placement algorithm.
          </Text>
        </Stack>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          disabled={!hasChanges || saving}
          loading={saving}
          onClick={handleSave}
        >
          Save pins
        </Button>
      </Group>

      <DragDropContext onDragEnd={handleDragEnd}>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <UnassignedTeamsPanel
            droppableId={UNASSIGNED_DROP_ID}
            teams={unpinnedTeams}
            saving={saving}
            emptyLabel="All teams are pinned to a slot."
          />

          {/* Entry slots */}
          <Card withBorder>
            <Stack gap="xs">
              <Text fw={600}>Entry-round slots</Text>
              <ScrollArea.Autosize mah={480}>
                <Stack gap="xs">
                  {entrySlots.map((slot) => {
                    const dropId = slotDroppableId(slot.gameId, slot.slotIndex);
                    const pinned = teamBySlot.get(dropId);
                    return (
                      <Droppable key={dropId} droppableId={dropId}>
                        {(provided, snapshot) => (
                          <Card
                            withBorder
                            padding="xs"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                              backgroundColor: snapshot.isDraggingOver
                                ? "var(--mantine-color-cyan-9)"
                                : undefined,
                            }}
                          >
                            <Stack gap={4}>
                              <Group justify="space-between">
                                <Text size="xs" c="dimmed">
                                  {slot.groupName} / {slot.roundName}
                                </Text>
                                <Badge size="xs" variant="light">
                                  Slot {slot.slotIndex + 1}
                                </Badge>
                              </Group>
                              <Text size="sm" fw={600}>
                                {slot.gameLabel}
                              </Text>
                              {pinned ? (
                                <Draggable
                                  draggableId={pinned.id}
                                  index={0}
                                  isDragDisabled={saving}
                                >
                                  {(drag) => (
                                    <Card
                                      withBorder
                                      padding="xs"
                                      ref={drag.innerRef}
                                      {...drag.draggableProps}
                                      style={{
                                        ...drag.draggableProps.style,
                                        cursor: "grab",
                                      }}
                                    >
                                      <Group justify="space-between">
                                        <Text
                                          size="sm"
                                          fw={500}
                                          {...drag.dragHandleProps}
                                          style={{ flex: 1 }}
                                        >
                                          {pinned.name}
                                        </Text>
                                        <ActionIcon
                                          size="sm"
                                          variant="subtle"
                                          color="red"
                                          aria-label={`Unpin ${pinned.name}`}
                                          onClick={() => handleUnpin(pinned.id)}
                                        >
                                          <IconX size={14} />
                                        </ActionIcon>
                                      </Group>
                                    </Card>
                                  )}
                                </Draggable>
                              ) : (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  ta="center"
                                  py="xs"
                                >
                                  Drop a team here
                                </Text>
                              )}
                              {provided.placeholder}
                            </Stack>
                          </Card>
                        )}
                      </Droppable>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            </Stack>
          </Card>
        </SimpleGrid>
      </DragDropContext>
    </Stack>
  );
};
